import { BaseEffect } from '../BaseEffect.js';

/**
 * Water ripple effect implementation
 * Creates realistic water ripples with dynamic wave propagation and interference
 */
export class WaterRippleEffect extends BaseEffect {
    constructor(shaderManager) {
        const defaultParams = {
            rippleSpeed: 1.0,      // Speed of ripple propagation
            rippleSize: 15.0,      // Size/scale of ripples
            rippleStrength: 0.1,   // Strength of the displacement
            dampening: 0.95,       // How quickly ripples fade out
            refraction: 0.5,       // Water refraction amount
            caustics: 0.3,         // Caustics (light focusing) intensity
            height: 0.4,           // Height/depth of water surface
            normalStrength: 1.0,   // Strength of normal mapping
            animate: true,         // Whether to animate automatically
            autoRippleSpeed: 1.0,  // Speed of automatic ripple generation
            autoRippleAmount: 0.5  // Amount of automatic ripples
        };

        super('water-ripples', shaderManager, defaultParams);
    }

    async init() {
        const vertexSource = `
            attribute vec2 a_position;
            attribute vec2 a_texCoord;
            varying vec2 v_texcoord;
            
            void main() {
                gl_Position = vec4(a_position, 0, 1);
                v_texcoord = a_texCoord;
            }
        `;

        const fragmentSource = `
            precision highp float;
            
            uniform sampler2D u_image;
            uniform float u_time;
            uniform vec2 u_resolution;
            uniform float u_rippleSpeed;
            uniform float u_rippleSize;
            uniform float u_rippleStrength;
            uniform float u_dampening;
            uniform float u_refraction;
            uniform float u_caustics;
            uniform float u_height;
            uniform float u_normalStrength;
            uniform float u_autoRippleSpeed;
            uniform float u_autoRippleAmount;
            
            varying vec2 v_texcoord;

            const float PI = 3.14159265359;
            const int NUM_WAVES = 8;

            // Improved random function
            float random(vec2 st) {
                return fract(sin(dot(st.xy, vec2(12.9898,78.233))) * 43758.5453123);
            }

            // 2D Noise
            float noise(vec2 st) {
                vec2 i = floor(st);
                vec2 f = fract(st);
                
                float a = random(i);
                float b = random(i + vec2(1.0, 0.0));
                float c = random(i + vec2(0.0, 1.0));
                float d = random(i + vec2(1.0, 1.0));

                vec2 u = f * f * (3.0 - 2.0 * f);
                return mix(a, b, u.x) + (c - a)* u.y * (1.0 - u.x) + (d - b) * u.x * u.y;
            }

            // Single wave function
            float wave(vec2 uv, vec2 center, float time, float frequency, float amplitude) {
                float dist = distance(uv, center);
                float wave = sin(dist * frequency - time) * amplitude;
                float damping = exp(-dist * u_dampening);
                return wave * damping;
            }

            // Calculate water surface height and normal
            vec3 getWaterSurface(vec2 uv, float time) {
                float height = 0.0;
                
                // Add automatic ripples
                for(int i = 0; i < NUM_WAVES; i++) {
                    float fi = float(i) / float(NUM_WAVES);
                    float speed = u_rippleSpeed * (0.8 + fi * 0.4);
                    float size = u_rippleSize * (0.9 + fi * 0.2);
                    
                    // Generate wave centers based on noise
                    vec2 center = vec2(
                        0.5 + cos(time * speed + fi * PI * 2.0) * 0.3,
                        0.5 + sin(time * speed + fi * PI * 2.0) * 0.3
                    );
                    
                    // Add wave contribution
                    height += wave(uv, center, time * speed, size, u_rippleStrength * u_autoRippleAmount);
                }

                // Calculate normals using finite differences
                vec2 dx = vec2(0.01, 0.0);
                vec2 dy = vec2(0.0, 0.01);
                
                // Calculate heights at neighboring points
                float heightRight = 0.0;
                float heightUp = 0.0;
                
                // Sample neighboring points
                for(int i = 0; i < NUM_WAVES; i++) {
                    float fi = float(i) / float(NUM_WAVES);
                    float speed = u_rippleSpeed * (0.8 + fi * 0.4);
                    float size = u_rippleSize * (0.9 + fi * 0.2);
                    
                    vec2 center = vec2(
                        0.5 + cos(time * speed + fi * PI * 2.0) * 0.3,
                        0.5 + sin(time * speed + fi * PI * 2.0) * 0.3
                    );
                    
                    heightRight += wave(uv + dx, center, time * speed, size, u_rippleStrength * u_autoRippleAmount);
                    heightUp += wave(uv + dy, center, time * speed, size, u_rippleStrength * u_autoRippleAmount);
                }
                
                // Calculate normal using height differentials
                float dHdx = (heightRight - height) / dx.x;
                float dHdy = (heightUp - height) / dy.y;
                vec2 normal = vec2(-dHdx, -dHdy) * u_normalStrength;
                
                return vec3(height, normal);
            }

            void main() {
                vec2 uv = v_texcoord;
                float aspect = u_resolution.x / u_resolution.y;
                vec2 scaledUV = uv;
                scaledUV.x *= aspect;
                
                float time = u_time * u_autoRippleSpeed;
                
                // Get water surface properties
                vec3 water = getWaterSurface(scaledUV, time);
                float height = water.x * u_height;
                vec2 normal = water.yz;
                
                // Calculate refraction offset
                vec2 refractOffset = normal * u_refraction * (0.1 + height);
                
                // Sample with refraction
                vec2 refractUV = uv + refractOffset;
                vec4 refractColor = texture2D(u_image, refractUV);
                
                // Add caustics
                float causticIntensity = pow(1.0 - abs(height), 2.0) * u_caustics;
                vec3 causticColor = vec3(1.0, 1.0, 0.95) * causticIntensity;
                
                // Combine effects
                vec3 finalColor = refractColor.rgb + causticColor;
                
                // Add subtle blue tint to simulate water depth
                finalColor += vec3(0.0, 0.1, 0.2) * height;
                
                gl_FragColor = vec4(finalColor, 1.0);
            }
        `;

        await super.init(vertexSource, fragmentSource);
    }

    setupUniforms() {
        super.setupUniforms();
        
        this.addUniform('u_rippleSpeed', { type: '1f' });
        this.addUniform('u_rippleSize', { type: '1f' });
        this.addUniform('u_rippleStrength', { type: '1f' });
        this.addUniform('u_dampening', { type: '1f' });
        this.addUniform('u_refraction', { type: '1f' });
        this.addUniform('u_caustics', { type: '1f' });
        this.addUniform('u_height', { type: '1f' });
        this.addUniform('u_normalStrength', { type: '1f' });
        this.addUniform('u_autoRippleSpeed', { type: '1f' });
        this.addUniform('u_autoRippleAmount', { type: '1f' });
    }

    updateUniforms(uniforms) {
        super.updateUniforms(uniforms);
        
        Object.assign(uniforms, {
            u_rippleSpeed: { value: this.params.rippleSpeed, type: '1f' },
            u_rippleSize: { value: this.params.rippleSize, type: '1f' },
            u_rippleStrength: { value: this.params.rippleStrength, type: '1f' },
            u_dampening: { value: this.params.dampening, type: '1f' },
            u_refraction: { value: this.params.refraction, type: '1f' },
            u_caustics: { value: this.params.caustics, type: '1f' },
            u_height: { value: this.params.height, type: '1f' },
            u_normalStrength: { value: this.params.normalStrength, type: '1f' },
            u_autoRippleSpeed: { value: this.params.animate ? this.params.autoRippleSpeed : 0.0, type: '1f' },
            u_autoRippleAmount: { value: this.params.autoRippleAmount, type: '1f' }
        });

        if (!this.params.animate) {
            uniforms.u_time.value = 0;
        }
    }

    getUIConfig() {
        return {
            rippleSpeed: {
                min: 0.1,
                max: 3.0,
                step: 0.1,
                label: 'Ripple Speed'
            },
            rippleSize: {
                min: 10,
                max: 50,
                step: 1,
                label: 'Ripple Size'
            },
            rippleStrength: {
                min: 0,
                max: 1,
                step: 0.01,
                label: 'Ripple Strength'
            },
            dampening: {
                min: 0.8,
                max: 0.99,
                step: 0.01,
                label: 'Dampening'
            },
            refraction: {
                min: 0,
                max: 1,
                step: 0.01,
                label: 'Refraction'
            },
            caustics: {
                min: 0,
                max: 1,
                step: 0.01,
                label: 'Caustics'
            },
            height: {
                min: 0,
                max: 1,
                step: 0.01,
                label: 'Water Height'
            },
            normalStrength: {
                min: 0.1,
                max: 2.0,
                step: 0.1,
                label: 'Normal Strength'
            },
            animate: {
                label: 'Auto Ripples'
            },
            autoRippleSpeed: {
                min: 0.1,
                max: 3.0,
                step: 0.1,
                label: 'Auto Speed'
            },
            autoRippleAmount: {
                min: 0,
                max: 1,
                step: 0.01,
                label: 'Auto Amount'
            }
        };
    }
} 